import React, { useState, useEffect, useCallback } from "react";
import {
  Popover,
  Box,
  Typography,
  IconButton,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  CircularProgress,
  useTheme,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MarkChatReadIcon from "@mui/icons-material/MarkChatRead";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import { useNavigate } from "react-router-dom";
import { notificationApis } from "../../Data/axios";

function NotificationPopover({ userId }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);

  const open = Boolean(anchorEl);
  const popoverId = open ? "notification-popover" : undefined;

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const response = await notificationApis.getByUserId(userId);
      if (response && response.data) {
        const sorted = [...response.data].sort(
          (a, b) => new Date(b.created_at || b.time) - new Date(a.created_at || a.time)
        );
        setNotifications(sorted);
        const unread = sorted.filter((n) => n.status === "1" || n.status === 1);
        setUnreadCount(unread.length);
      }
    } catch (error) {
      // silent fail for polling
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAllRead = async () => {
    if (!userId || markingRead) return;
    setMarkingRead(true);
    try {
      await notificationApis.markAllRead(userId);
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, status: "0" }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark notifications as read:", error);
    } finally {
      setMarkingRead(false);
    }
  };

  const handleNotificationClick = (notification) => {
    handleClose();
    if (notification.redirect_id) {
      navigate(`/tickets/${notification.redirect_id}`);
    }
  };

  const getNotificationIcon = (type) => {
    if (type === "ticket") {
      return <ConfirmationNumberIcon sx={{ color: "#1976d2" }} />;
    }
    return <NotificationsIcon sx={{ color: "#f57c00" }} />;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    try {
      const date = new Date(timeStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHrs = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHrs < 24) return `${diffHrs}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return "";
    }
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        aria-label="notifications"
        aria-controls={popoverId}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        sx={{ color: "#fff" }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          sx={{
            "& .MuiBadge-badge": {
              backgroundColor: "#f44336",
              color: "#fff",
              fontSize: "11px",
              height: "20px",
              minWidth: "20px",
            },
          }}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 480,
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1.5,
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#fafafa",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "16px" }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={
                markingRead ? (
                  <CircularProgress size={14} />
                ) : (
                  <MarkChatReadIcon />
                )
              }
              onClick={handleMarkAllRead}
              disabled={markingRead}
              sx={{ textTransform: "none", fontSize: "12px" }}
            >
              Mark all read
            </Button>
          )}
        </Box>

        {loading && notifications.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <NotificationsIcon sx={{ fontSize: 48, color: "#bdbdbd", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0, maxHeight: 400, overflow: "auto" }}>
            {notifications.slice(0, 20).map((notification, index) => (
              <React.Fragment key={notification.id || index}>
                <ListItem
                  sx={{
                    cursor: notification.redirect_id ? "pointer" : "default",
                    backgroundColor:
                      notification.status === "1" || notification.status === 1
                        ? "#e3f2fd"
                        : "transparent",
                    "&:hover": {
                      backgroundColor:
                        notification.redirect_id ? "#f5f5f5" : "transparent",
                    },
                    py: 1.5,
                  }}
                  onClick={() =>
                    notification.redirect_id && handleNotificationClick(notification)
                  }
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getNotificationIcon(notification.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight:
                              notification.status === "1" || notification.status === 1
                                ? 600
                                : 400,
                            fontSize: "13px",
                            lineHeight: 1.4,
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "#999", whiteSpace: "nowrap", ml: 1 }}
                        >
                          {formatTime(notification.time)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#666",
                          fontSize: "12px",
                          lineHeight: 1.4,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {notification.body}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
}

export default NotificationPopover;
